import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CheckCircle2, Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { TextField, AreaField, SelectField, ConsentBox, Honeypot, Eyebrow } from "@/components/form";
import { usePageMeta } from "@/hooks/usePageMeta";
import { api, apiError } from "@/lib/api";

const TYPES = [
  { value: "Church property opportunity", label: "Church property opportunity" },
  { value: "Ministry support inquiry", label: "Ministry support inquiry" },
  { value: "Artist introduction", label: "Artist introduction" },
  { value: "Business directory question", label: "Business directory question" },
  { value: "General partnership conversation", label: "General partnership conversation" },
];

const EMPTY = { inquiry_type: "", name: "", email: "", phone: "", message: "", consent: false, hp_field: "" };

export default function Connect() {
  usePageMeta("Connect & Partner",
    "Reach out about church property opportunities, ministry support, artists, the business directory, or partnership.");
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [settings, setSettings] = useState({});
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  useEffect(() => { api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.inquiry_type) return toast.error("Please choose an inquiry type.");
    if (!form.consent) return toast.error("Please provide consent to be contacted.");
    setSubmitting(true);
    try { await api.post("/inquiries/contact", form); setDone(true); window.scrollTo({ top: 0 }); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSubmitting(false); }
  };

  const hasContact = settings.contact_email || settings.contact_phone || settings.contact_address;

  return (
    <div>
      <PageHero
        eyebrow="Connect & Partner"
        title="We'd love to hear from you"
        subtitle="Whether you're a church leader, a ministry, an artist, a business owner, or simply want to partner in this work — choose a topic below and send us a note."
        image="/ju-stairway.webp"
      />

      <section className="mx-auto max-w-5xl px-5 lg:px-8 py-16 grid gap-12 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {done ? (
            <Reveal>
              <div className="rounded-xl border border-emerald/30 bg-emerald/5 p-10 text-center" data-testid="connect-success">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald" />
                <h2 className="mt-4 font-display text-3xl text-navy font-semibold">Thank you</h2>
                <p className="mt-3 text-muted-foreground">We've received your message and will be in touch.</p>
                <Button className="mt-6 rounded-full bg-emerald hover:bg-emerald-dark text-white px-8" onClick={() => { setForm(EMPTY); setDone(false); }}>Send another</Button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <Eyebrow>Send a message</Eyebrow>
              <h2 className="mt-3 font-display text-3xl font-semibold text-navy tracking-tight">Start the conversation</h2>
              <form onSubmit={submit} className="mt-8 grid gap-5" data-testid="connect-form">
                <Honeypot value={form.hp_field} onChange={set("hp_field")} />
                <SelectField label="What is this about?" name="inquiry_type" required
                  value={form.inquiry_type} onValueChange={set("inquiry_type")} placeholder="Choose a topic" options={TYPES} />
                <div className="grid gap-5 sm:grid-cols-2">
                  <TextField label="Your name" name="name" required value={form.name} onChange={onInput("name")} />
                  <TextField label="Email" name="email" type="email" required value={form.email} onChange={onInput("email")} />
                </div>
                <TextField label="Phone (optional)" name="phone" value={form.phone} onChange={onInput("phone")} />
                <AreaField label="Your message" name="message" required rows={5} value={form.message} onChange={onInput("message")} />
                <ConsentBox checked={form.consent} onChange={set("consent")}>I consent to be contacted by Lord Almighty about this message.</ConsentBox>
                <div><Button type="submit" disabled={submitting} className="rounded-full bg-emerald hover:bg-emerald-dark text-white px-10 py-6 text-base" data-testid="connect-submit">{submitting ? "Sending…" : "Send Message"}</Button></div>
              </form>
            </Reveal>
          )}
        </div>

        <aside>
          <Reveal delay={0.1}>
            <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
              <h3 className="font-display text-xl text-navy font-semibold">Reach us</h3>
              {hasContact ? (
                <div className="mt-4 space-y-3 text-sm">
                  {settings.contact_email && <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-2 text-navy hover:text-emerald break-all"><Mail className="h-4 w-4" /> {settings.contact_email}</a>}
                  {settings.contact_phone && <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-2 text-navy hover:text-emerald"><Phone className="h-4 w-4" /> {settings.contact_phone}</a>}
                  {settings.contact_address && <p className="flex items-start gap-2 text-navy"><MapPin className="h-4 w-4 mt-0.5" /> {settings.contact_address}</p>}
                </div>
              ) : (
                <p className="mt-4 text-sm text-muted-foreground">
                  Our public contact details will appear here once they are provided. In the meantime,
                  the form is the best way to reach us.
                </p>
              )}
            </div>
            <p className="mt-5 text-xs text-muted-foreground leading-relaxed">
              We are not currently accepting online donations. Any giving option will be published only
              once an approved payment account and organization details are in place. We do not claim
              contributions are tax-deductible without confirmation.
            </p>
          </Reveal>
        </aside>
      </section>
    </div>
  );
}
